import { Link, NavLink } from "react-router-dom";
import {
  Bank,
  Folder,
  Gear,
  UserList,
  Sparkle,
  Tag,
  ListChecks,
  SignOut,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const navItem = ({ isActive }) =>
  `relative px-1 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
    isActive ? "text-brand-forest" : "text-brand-ink/70 hover:text-brand-forest"
  }`;

function Brand() {
  return (
    <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2.5 group shrink-0">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-brand-forest text-brand-bone">
        <Bank weight="duotone" size={20} />
      </span>
      <span className="hidden sm:flex flex-col leading-tight">
        <span className="font-serif text-[15px] text-brand-forest tracking-tight">
          Build A Bank
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">
          instead of borrowing from one
        </span>
      </span>
    </Link>
  );
}

function ExamplesLink() {
  return (
    <NavLink to="/examples" className={navItem} data-testid="nav-examples">
      <span className="inline-flex items-center gap-1.5">
        <ListChecks size={15} weight="duotone" /> Examples
      </span>
    </NavLink>
  );
}

function UserChip({ user, logout }) {
  return (
    <div className="flex items-center gap-2 pl-2 border-l border-brand-line shrink-0">
      {user.picture ? (
        <img
          src={user.picture}
          alt={user.name}
          referrerPolicy="no-referrer"
          data-testid="user-avatar"
          className="w-7 h-7 rounded-full border border-brand-line object-cover"
        />
      ) : (
        <span
          data-testid="user-avatar"
          className="w-7 h-7 rounded-full bg-brand-forest text-brand-bone text-xs font-bold inline-flex items-center justify-center"
        >
          {(user.name || user.email || "?").charAt(0).toUpperCase()}
        </span>
      )}
      <span className="hidden lg:block text-xs text-brand-ink/70 max-w-[120px] truncate" data-testid="user-name">
        {user.name || user.email}
      </span>
      <button
        onClick={logout}
        data-testid="btn-logout"
        title="Sign out"
        className="text-brand-muted hover:text-red-700 transition-colors"
      >
        <SignOut size={17} weight="bold" />
      </button>
    </div>
  );
}

function AuthedNav({ user, logout }) {
  const publicLink = user.ref_code
    ? `/quick-breakdown?ref=${user.ref_code}`
    : "/quick-breakdown";
  return (
    <>
      <NavLink to="/calculator" className={navItem} data-testid="nav-calculator">
        Calculator
      </NavLink>
      <ExamplesLink />
      <NavLink to="/scenarios" className={navItem} data-testid="nav-scenarios">
        <span className="inline-flex items-center gap-1.5">
          <Folder size={15} weight="duotone" /> Scenarios
        </span>
      </NavLink>
      <NavLink to="/leads" className={navItem} data-testid="nav-leads">
        <span className="inline-flex items-center gap-1.5">
          <UserList size={15} weight="duotone" /> Leads
        </span>
      </NavLink>
      <NavLink to="/pricing" className={navItem} data-testid="nav-pricing">
        <span className="inline-flex items-center gap-1.5">
          <Tag size={15} weight="duotone" /> Pricing
        </span>
      </NavLink>
      <NavLink to="/settings" className={navItem} data-testid="nav-settings">
        <span className="inline-flex items-center gap-1.5">
          <Gear size={15} weight="duotone" /> Settings
        </span>
      </NavLink>
      {user.role === "admin" && (
        <NavLink to="/admin" className={navItem} data-testid="nav-admin">
          <span className="inline-flex items-center gap-1.5 text-brand-copper">
            <ShieldCheck size={15} weight="duotone" /> Admin
          </span>
        </NavLink>
      )}
      <Link
        to={publicLink}
        target="_blank"
        rel="noreferrer"
        data-testid="header-cta-public"
        className="hidden xl:inline-flex items-center gap-1.5 rounded-md border border-brand-copper/40 bg-brand-copper/10 hover:bg-brand-copper/15 text-brand-copper px-3 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap"
        title="Open your public lead-capture page"
      >
        <Sparkle size={13} weight="fill" /> Public link
      </Link>
      <UserChip user={user} logout={logout} />
    </>
  );
}

function PublicNav({ loading }) {
  return (
    <>
      <ExamplesLink />
      {!loading && (
        <Link
          to="/login"
          data-testid="nav-signin"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-forest hover:bg-brand-forestHover text-brand-bone px-4 py-2 text-xs font-semibold transition-colors whitespace-nowrap"
        >
          Advisor sign in
        </Link>
      )}
    </>
  );
}

export default function Header() {
  const { user, loading, logout } = useAuth();
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-brand-line bg-brand-bone/80 backdrop-blur-xl no-print"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 gap-4">
        <Brand />
        <nav className="flex items-center gap-4 lg:gap-5 overflow-x-auto">
          {user ? <AuthedNav user={user} logout={logout} /> : <PublicNav loading={loading} />}
        </nav>
      </div>
    </header>
  );
}
